"use client";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../utils/classNames";
import { useTranslation } from "../../hooks/useTranslation";
import { LocalizedLink } from "../../components/LocalizedLink";
import { landingFaqTranslations } from "../../i18n/translations/pages";

interface FaqItem {
  question: string;
  answer: string;
}

export function SimpleFaqsWithBackground() {
  const [open, setOpen] = useState<string | null>(null);
  const { locale } = useTranslation();
  
  // Get FAQ items from the dedicated translations file
  const translations = landingFaqTranslations[locale as keyof typeof landingFaqTranslations] || landingFaqTranslations.en;
  const faqItems: FaqItem[] = translations.items;
  
  return (
    <div className="mx-auto grid w-full max-w-7xl gap-4 px-4 py-20 md:px-8 md:py-40">
      <h2 className="text-center text-4xl font-medium tracking-tight text-neutral-600 dark:text-neutral-50 md:text-5xl">
        {translations.title}
      </h2>
      <p className="mx-auto max-w-lg text-center text-base text-neutral-600 dark:text-neutral-50">
        {translations.subtitle}{" "}
        <LocalizedLink
          to="/faq"
          className="font-medium text-primary-600 hover:text-primary-500 transition-colors duration-200"
        >
          {translations.completeFaqLink}
        </LocalizedLink>
      </p>
      <div className="mx-auto mt-10 w-full max-w-3xl">
        {faqItems.map((faq: FaqItem, index: number) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            open={open}
            setOpen={setOpen}
          />
        ))}
      </div>
    </div>
  );
}

const FAQItem = ({
  question,
  answer,
  setOpen,
  open,
}: {
  question: string;
  answer: string;
  open: string | null;
  setOpen: (open: string | null) => void;
}) => {
  const isOpen = open === question;

  return (
    <div
      className="mb-8 w-full cursor-pointer rounded-2xl bg-white p-4 shadow-sm border border-gray-200 hover:border-primary-500/20 hover:shadow-md transition-all duration-300 ease-out dark:bg-neutral-900"
      onClick={() => {
        if (isOpen) {
          setOpen(null);
        } else {
          setOpen(question);
        }
      }}
    >
      <div className="flex items-start">
        <div className="relative mr-4 mt-1 h-6 w-6 flex-shrink-0">
          <ChevronDown
            className={cn(
              "absolute inset-0 h-6 w-6 transform text-black transition-all duration-200 dark:text-white",
              isOpen && "rotate-90 scale-0",
            )}
          />
          <ChevronUp
            className={cn(
              "absolute inset-0 h-6 w-6 rotate-90 scale-0 transform text-black transition-all duration-200 dark:text-white",
              isOpen && "rotate-0 scale-100",
            )}
          />
        </div>
        <div>
          <h3 className="text-lg font-medium text-neutral-700 dark:text-neutral-200">
            {question}
          </h3>
          <AnimatePresence mode="wait">
            {isOpen && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: "auto" }}
                exit={{ height: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="overflow-hidden text-neutral-500 dark:text-neutral-400"
              >
                {answer.split("").map((line, index) => (
                  <motion.span
                    initial={{ opacity: 0, filter: "blur(5px)" }}
                    animate={{ opacity: 1, filter: "blur(0px)" }}
                    exit={{ opacity: 0, filter: "blur(0px)" }}
                    transition={{
                      duration: 0.2,
                      ease: "easeOut",
                      delay: index * 0.005,
                    }}
                    key={index}
                  >
                    {line}
                  </motion.span>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
